// See EnrollHostsDrawer.spec.md for EHD-FR-002
import { makeStyles } from '@material-ui/styles'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getBlueprints } from 'app/plugins/openstack/components/infrastructure/actions'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import DropdownField from 'core/components/validatedForm/DropdownField'
import Badge from 'core/elements/badge'
import Card from 'core/elements/card'
import CardHeader from 'core/elements/card/CardHeader'
import Divider from 'core/elements/Divider'
import { dropdownDefaultWidth } from 'core/elements/dropdown/constants'
import DropdownItemWithTooltip from 'core/elements/dropdown/DropdownItemWithTooltip'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import type Theme from 'core/themes/model'
import {
  DRRStatusCell,
  GpuCellComponent,
} from 'openstack/components/infrastructure/clusters/ClusterHostGroupsListPage'
import { AllTenantsClusterHostGroupsPicklist } from 'openstack/components/infrastructure/clusters/ClusterHostGroupsPicklist'
import { clusterHostGroupsAllTenantsSelector } from 'openstack/components/infrastructure/clusters/selectors'
import { VMHAStatusForClusterList } from 'openstack/components/infrastructure/clusters/VMHAStatusCell'
import type { IResmgrHostSelector } from 'openstack/components/infrastructure/model'
import { clusterOptionFor } from '../helpers'
import type { EnrollStepProps } from './EnrollHostsDrawer'
import StepSection from './StepSection'

// EHD-FR-002: a cluster is disabled if it's not allowed for ANY selected host (first matching
// tooltip wins); if it's allowed but warns for some host, show that warning instead
const optionForHosts = (cluster, hosts: IResmgrHostSelector[]) => {
  let warning = null
  for (const host of hosts) {
    const option = clusterOptionFor(cluster, host)
    if (option.disabled) return option
    if (option.tooltip && !warning) warning = option
  }
  return warning || { disabled: false }
}

export default function SelectClusterStep({ selection, onChange, hosts, errors }: EnrollStepProps) {
  const classes = useStyles()
  const clusterHostGroups = useSelector(clusterHostGroupsAllTenantsSelector)
  const [blueprintName, setBlueprintName] = useState('')

  useEffect(() => {
    let active = true
    getBlueprints()
      .then((blueprints: Array<{ name?: string }>) => {
        if (active) setBlueprintName(blueprints?.[0]?.name || '')
      })
      .catch(() => {
        if (active) setBlueprintName('')
      })
    return () => {
      active = false
    }
  }, [])

  const selectedCluster = useMemo(
    () => clusterHostGroups.find((cluster) => cluster.name === selection.clusterName),
    [clusterHostGroups, selection.clusterName],
  )

  // Label and value styling follows the Stretched Cluster overview fields
  const detailFields: Array<{ label: string; value: ReactNode }> | undefined = selectedCluster && [
    {
      label: 'Hosts',
      value: <Text variant="caption1">{selectedCluster.hostlist?.length ?? 0}</Text>,
    },
    { label: 'Blueprint', value: <Text variant="caption1">{blueprintName || '-'}</Text> },
    {
      label: 'GPU Mode',
      value: <GpuCellComponent value={selectedCluster.gpu?.enabled} item={selectedCluster} />,
    },
    {
      label: 'VM High Availability',
      value: <VMHAStatusForClusterList value={selectedCluster.vmhaStatus} item={selectedCluster} />,
    },
    {
      label: 'Dynamic Resource Rebalancing',
      value: <DRRStatusCell value={selectedCluster.autoResourceRebalancing?.enabled} />,
    },
  ]

  return (
    <div className={classes.root}>
      <StepSection
        title="Select Cluster"
        description="All hosts join the same cluster as hypervisors. The cluster decides high availability, resource rebalancing, GPU handling and guest CPU settings for every host in it."
      >
        <Divider className={classes.divider} />
        <CheckboxField
          id="hypervisor"
          label="Apply Hypervisor Role"
          aria-label="Apply Hypervisor Role"
          value={selection.hypervisor}
          onChange={(value) =>
            onChange({ hypervisor: value, ...(value ? {} : { clusterName: '' }) })
          }
        />
        <div>
          <DropdownField
            id="clusterName"
            label="Hypervisor Cluster"
            DropdownComponent={AllTenantsClusterHostGroupsPicklist}
            width={dropdownDefaultWidth}
            value={selection.clusterName}
            onChange={(value: string) => onChange({ clusterName: value })}
            disabled={!selection.hypervisor}
            placeholder="Select cluster"
            optionMapFn={(cluster) => {
              const option = optionForHosts(cluster, hosts)
              if (!option.tooltip) {
                return { label: cluster?.name, value: cluster?.name, disabled: false }
              }
              return {
                content: (
                  <DropdownItemWithTooltip
                    label={cluster?.name}
                    tooltip={option.tooltip}
                    icon={option.icon}
                    disabled={option.disabled}
                  />
                ),
                value: cluster?.name,
                disabled: option.disabled,
              }
            }}
          />
          {!selection.hypervisor && (
            <Text variant="caption1" className={classes.helperText}>
              Select Apply Hypervisor Role to choose a cluster.
            </Text>
          )}
          {errors?.clusterName && (
            <Text variant="caption1" className={classes.errorText}>
              {errors.clusterName}
            </Text>
          )}
        </div>
      </StepSection>
      {detailFields && (
        <Card
          title={
            <CardHeader className={classes.detailsHeader}>
              Cluster Details
              <Badge text="Read only" variant="default" />
            </CardHeader>
          }
        >
          {/* Fills top to bottom: Hosts, Blueprint, GPU on the left; VM HA, DRR on the right */}
          <div className={classes.detailsGrid}>
            {detailFields.map(({ label, value }) => (
              <div key={label} className={classes.detailsField}>
                <Text variant="body2" className={classes.detailsLabel}>
                  {label}
                </Text>
                {value}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  helperText: {
    color: colors.grey[500],
    fontSize: 12,
    fontWeight: 'normal',
    marginTop: theme.spacing(0.5),
    display: 'block',
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
    display: 'block',
  },
  detailsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  // Two columns filled top to bottom, three rows on the left
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'repeat(3, auto)',
    gridAutoFlow: 'column',
    gap: theme.spacing(2, 4),
    padding: theme.spacing(1, 0),
  },
  // Same as the Stretched Cluster overview configField / configLabel
  detailsField: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  detailsLabel: {
    color: theme.components.card.passiveText,
    fontSize: 12,
  },
}))
